export type SfxName =
  | "match_clear"
  | "cascade_chain"
  | "bomb_explode"
  | "bomb_4"
  | "bomb_5"
  | "wipe_sweep"
  | "crate_break"
  | "rotation"
  | "left_rotate"
  | "right_rotate"
  | "warning_alert"
  | "level_up"
  | "new_color"
  | "game_over"
  | "button_click"
  | "groove_milestone"
  | "swap_valid"
  | "swap_reject"
  | "tile_pickup"
  | "cascade_step"
  | "extra_life"
  | "key_earned"
  | "life_lost"
  | "collapse_land"
  | "coin_gain"
  | "buy_gems"
  | "shop_deny"
  | "powerup_arm"
  | "powerup_cast"
  | "transmute"
  | "column_cutter"
  | "row_ripper"
  | "menu_whoosh"
  | "toggle_click"
  | "score_tick"
  | "new_highscore"
  | "skin_equip"
  | "gem_claim"
  | "daily_treasure"
  | "score_milestone"
  | "menu_hover";

function playNote(
  ctx: AudioContext,
  dest: AudioNode,
  freq: number,
  dur: number,
  delay: number,
  gain: number,
  type: OscillatorType = "square"
): void {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.value = 0.0001;
  osc.connect(g);
  g.connect(dest);
  const now = ctx.currentTime + delay;
  g.gain.exponentialRampToValueAtTime(Math.max(gain, 0.0001), now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  osc.start(now);
  osc.stop(now + dur + 0.02);
}

function playNoiseBurst(
  ctx: AudioContext,
  dest: AudioNode,
  dur: number,
  cutoff: number,
  gain: number
): void {
  const bufferSize = Math.ceil(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = cutoff;
  const g = ctx.createGain();
  g.gain.value = gain;
  const now = ctx.currentTime;
  g.gain.exponentialRampToValueAtTime(Math.max(gain, 0.0001), now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  source.connect(filter);
  filter.connect(g);
  g.connect(dest);
  source.start(now);
  source.stop(now + dur + 0.02);
}

function playSweep(
  ctx: AudioContext,
  dest: AudioNode,
  startFreq: number,
  endFreq: number,
  dur: number,
  gain: number
): void {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "sawtooth";
  osc.frequency.value = startFreq;
  g.gain.value = 0.0001;
  osc.connect(g);
  g.connect(dest);
  const now = ctx.currentTime;
  osc.frequency.exponentialRampToValueAtTime(Math.max(endFreq, 20), now + dur);
  g.gain.exponentialRampToValueAtTime(Math.max(gain, 0.0001), now + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  osc.start(now);
  osc.stop(now + dur + 0.02);
}

function playFilteredNoise(
  ctx: AudioContext,
  dest: AudioNode,
  dur: number,
  lowFreq: number,
  highFreq: number,
  gain: number
): void {
  const bufferSize = Math.ceil(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = (lowFreq + highFreq) / 2;
  bp.Q.value = 1;
  const now = ctx.currentTime;
  bp.frequency.linearRampToValueAtTime(highFreq, now + dur * 0.3);
  bp.frequency.linearRampToValueAtTime(lowFreq, now + dur);
  const g = ctx.createGain();
  g.gain.value = gain;
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  source.connect(bp);
  bp.connect(g);
  g.connect(dest);
  source.start(now);
  source.stop(now + dur + 0.02);
}

// Gain levels per skill guidance (music-sfx-selection):
// Primary SFX (-8 to -12 dB) = 0.4 – 0.25   (match clear, bomb, cascade)
// Feedback SFX (-10 to -6 dB) = 0.3 – 0.5   (level up, extra life, groove)
// Secondary SFX (-14 to -18 dB) = 0.2 – 0.12 (hover, pickup, land)
const SFX_GAIN: Record<SfxName, number> = {
  match_clear: 0.35,
  cascade_chain: 0.38,
  bomb_explode: 0.45,
  bomb_4: 0.45,
  bomb_5: 0.495,
  wipe_sweep: 0.24,
  crate_break: 0.28,
  rotation: 0.25,
  left_rotate: 0.25,
  right_rotate: 0.30,
  warning_alert: 0.30,
  level_up: 0.50,
  new_color: 0.45,
  game_over: 0.45,
  button_click: 0.18,
  groove_milestone: 0.40,
  swap_valid: 0.25,
  swap_reject: 0.28,
  tile_pickup: 0.15,
  cascade_step: 0.32,
  extra_life: 0.48,
  key_earned: 0.30,
  life_lost: 0.42,
  collapse_land: 0.14,
  coin_gain: 0.24,
  buy_gems: 0.24,
  shop_deny: 0.22,
  powerup_arm: 0.2,
  powerup_cast: 0.28,
  transmute: 0.28,
  column_cutter: 0.24,
  row_ripper: 0.24,
  menu_whoosh: 0.12,
  toggle_click: 0.15,
  score_tick: 0.08,
  new_highscore: 0.45,
  skin_equip: 0.20,
  gem_claim: 0.30,
  daily_treasure: 0.30,
  score_milestone: 0.25,
  menu_hover: 0.3
};

// Pitch randomization range (±%) to prevent auditory fatigue on repeated sounds
const SFX_PITCH_JITTER: Partial<Record<SfxName, number>> = {
  cascade_step: 0.04,
  match_clear: 0.03,
  collapse_land: 0.06,
  tile_pickup: 0.05,
  swap_valid: 0.02,
  swap_reject: 0.03,
  button_click: 0.04,
  coin_gain: 0.02,
  buy_gems: 0.02,
  powerup_arm: 0.03,
  score_tick: 0.06,
  menu_hover: 0.06
};

type SfxRecipe = (ctx: AudioContext, dest: AudioNode, pitchMultiplier: number) => void;

const SFX_RECIPES: Record<SfxName, SfxRecipe> = {
  match_clear(ctx, dest, pm) {
    playNote(ctx, dest, 523 * pm, 0.04, 0, 0.12);
    playNote(ctx, dest, 659 * pm, 0.04, 0.04, 0.10);
    playNote(ctx, dest, 784 * pm, 0.06, 0.08, 0.08);
  },
  cascade_chain(ctx, dest, pm) {
    playNote(ctx, dest, 660 * pm, 0.03, 0, 0.11);
    playNote(ctx, dest, 880 * pm, 0.05, 0.03, 0.09);
  },
  bomb_explode(ctx, dest) {
    playNoiseBurst(ctx, dest, 0.15, 800, 0.14);
    playNote(ctx, dest, 120, 0.2, 0, 0.16, "sine");
  },
  bomb_4(ctx, dest) {
    playNoiseBurst(ctx, dest, 0.15, 800, 0.14);
    playNote(ctx, dest, 120, 0.2, 0, 0.16, "sine");
  },
  bomb_5(ctx, dest) {
    playNoiseBurst(ctx, dest, 0.17, 700, 0.154);
    playNote(ctx, dest, 110, 0.22, 0, 0.176, "sine");
  },
  wipe_sweep(ctx, dest) {
    playSweep(ctx, dest, 2000, 200, 0.3, 0.10);
  },
  crate_break(ctx, dest) {
    playNoiseBurst(ctx, dest, 0.08, 1200, 0.10);
    playNote(ctx, dest, 210, 0.08, 0.01, 0.08, "square");
    playNote(ctx, dest, 160, 0.10, 0.05, 0.07, "triangle");
  },
  rotation(ctx, dest) {
    playFilteredNoise(ctx, dest, 0.25, 400, 1200, 0.10);
  },
  left_rotate(ctx, dest) {
    playFilteredNoise(ctx, dest, 0.25, 400, 1200, 0.10);
  },
  right_rotate(ctx, dest) {
    playFilteredNoise(ctx, dest, 0.25, 400, 1200, 0.10);
  },
  warning_alert(ctx, dest) {
    playNote(ctx, dest, 880, 0.06, 0, 0.08, "square");
    playNote(ctx, dest, 880, 0.06, 0.1, 0.08, "square");
  },
  level_up(ctx, dest) {
    playNote(ctx, dest, 523, 0.08, 0, 0.12);
    playNote(ctx, dest, 659, 0.08, 0.08, 0.12);
    playNote(ctx, dest, 784, 0.08, 0.16, 0.12);
    playNote(ctx, dest, 1047, 0.15, 0.24, 0.14);
  },
  // Ascending shimmer chime for new color unlock
  new_color(ctx, dest) {
    playNote(ctx, dest, 1047, 0.06, 0, 0.10, "sine");
    playNote(ctx, dest, 1319, 0.06, 0.05, 0.10, "sine");
    playNote(ctx, dest, 1568, 0.06, 0.10, 0.10, "sine");
    playNote(ctx, dest, 2093, 0.12, 0.15, 0.12, "sine");
    playFilteredNoise(ctx, dest, 0.18, 2000, 6000, 0.04);
  },
  game_over(ctx, dest) {
    playNote(ctx, dest, 330, 0.12, 0, 0.12, "sawtooth");
    playNote(ctx, dest, 262, 0.12, 0.15, 0.12, "sawtooth");
    playNote(ctx, dest, 220, 0.25, 0.30, 0.14, "sawtooth");
  },
  button_click(ctx, dest) {
    playNote(ctx, dest, 1200, 0.015, 0, 0.05, "square");
  },
  groove_milestone(ctx, dest) {
    playNote(ctx, dest, 1320, 0.1, 0, 0.08);
    playNote(ctx, dest, 1760, 0.15, 0.05, 0.06);
  },

  // Satisfying "click-slide" on valid swap
  swap_valid(ctx, dest) {
    playNote(ctx, dest, 880, 0.025, 0, 0.07, "sine");
    playNote(ctx, dest, 1100, 0.03, 0.02, 0.06, "sine");
  },

  // Dull thud on rejected swap
  swap_reject(ctx, dest) {
    playNote(ctx, dest, 180, 0.06, 0, 0.10, "sine");
    playNoiseBurst(ctx, dest, 0.04, 400, 0.06);
  },

  // Light pop when picking up a tile
  tile_pickup(ctx, dest) {
    playNote(ctx, dest, 1400, 0.015, 0, 0.04, "sine");
  },

  // Rising note per cascade step — pitchMultiplier controls the pitch escalation
  cascade_step(ctx, dest, pm) {
    playNote(ctx, dest, 523 * pm, 0.05, 0, 0.10, "sine");
    playNote(ctx, dest, 784 * pm, 0.06, 0.03, 0.07, "triangle");
    playNoiseBurst(ctx, dest, 0.03, 1200 * pm, 0.04);
  },

  // Joyful chime for extra life
  extra_life(ctx, dest) {
    playNote(ctx, dest, 784, 0.06, 0, 0.10, "sine");
    playNote(ctx, dest, 988, 0.06, 0.06, 0.10, "sine");
    playNote(ctx, dest, 1175, 0.06, 0.12, 0.10, "sine");
    playNote(ctx, dest, 1568, 0.12, 0.18, 0.12, "sine");
  },

  // Metallic key pickup sound
  key_earned(ctx, dest) {
    playNote(ctx, dest, 1760, 0.04, 0, 0.06, "triangle");
    playNote(ctx, dest, 2200, 0.06, 0.03, 0.05, "triangle");
    playNoiseBurst(ctx, dest, 0.03, 3000, 0.03);
  },

  // Descending tone when losing a life
  life_lost(ctx, dest) {
    playNote(ctx, dest, 440, 0.08, 0, 0.12, "sawtooth");
    playNote(ctx, dest, 330, 0.08, 0.08, 0.12, "sawtooth");
    playNote(ctx, dest, 220, 0.15, 0.16, 0.14, "sawtooth");
    playNoiseBurst(ctx, dest, 0.08, 600, 0.08);
  },

  // Soft thump when tiles land after collapse
  collapse_land(ctx, dest, pm) {
    playNote(ctx, dest, 160 * pm, 0.04, 0, 0.06, "sine");
    playNoiseBurst(ctx, dest, 0.025, 500, 0.03);
  },

  coin_gain(ctx, dest) {
    playNote(ctx, dest, 1047, 0.035, 0, 0.07, "triangle");
    playNote(ctx, dest, 1319, 0.05, 0.03, 0.06, "triangle");
  },
  buy_gems(ctx, dest) {
    playNote(ctx, dest, 1047, 0.035, 0, 0.07, "triangle");
    playNote(ctx, dest, 1319, 0.05, 0.03, 0.06, "triangle");
  },

  shop_deny(ctx, dest) {
    playNote(ctx, dest, 220, 0.07, 0, 0.08, "square");
    playNote(ctx, dest, 175, 0.08, 0.05, 0.07, "square");
    playNoiseBurst(ctx, dest, 0.03, 500, 0.04);
  },

  powerup_arm(ctx, dest) {
    playNote(ctx, dest, 740, 0.03, 0, 0.06, "sine");
    playNote(ctx, dest, 988, 0.04, 0.02, 0.05, "triangle");
  },

  powerup_cast(ctx, dest) {
    playSweep(ctx, dest, 450, 1200, 0.12, 0.09);
    playNoiseBurst(ctx, dest, 0.05, 1800, 0.05);
  },
  transmute(ctx, dest) {
    playSweep(ctx, dest, 450, 1200, 0.12, 0.09);
    playNoiseBurst(ctx, dest, 0.05, 1800, 0.05);
  },
  column_cutter(ctx, dest, pm) {
    playSweep(ctx, dest, 2000 * pm, 200 * pm, 0.3, 0.10);
  },
  row_ripper(ctx, dest, pm) {
    playSweep(ctx, dest, 2000 * pm, 200 * pm, 0.3, 0.10);
  },

  // Soft swoosh for page transitions
  menu_whoosh(ctx, dest) {
    playFilteredNoise(ctx, dest, 0.08, 800, 3000, 0.06);
    playSweep(ctx, dest, 600, 900, 0.06, 0.03);
  },

  // Higher-pitched click for toggle actions
  toggle_click(ctx, dest) {
    playNote(ctx, dest, 1600, 0.012, 0, 0.06, "square");
    playNote(ctx, dest, 2000, 0.015, 0.01, 0.04, "sine");
  },

  // Very short click for score counting on game over
  score_tick(ctx, dest, pm) {
    playNote(ctx, dest, 2000 * pm, 0.008, 0, 0.03, "square");
  },

  // Ascending celebration fanfare for new high score
  new_highscore(ctx, dest) {
    playNote(ctx, dest, 1047, 0.08, 0, 0.10, "sine");     // C6
    playNote(ctx, dest, 1319, 0.08, 0.07, 0.10, "sine");   // E6
    playNote(ctx, dest, 1568, 0.08, 0.14, 0.10, "sine");   // G6
    playNote(ctx, dest, 2093, 0.15, 0.21, 0.14, "sine");   // C7
    playFilteredNoise(ctx, dest, 0.20, 3000, 8000, 0.04);   // shimmer burst
  },

  // Two-tone ascending confirmation chime for skin equip
  skin_equip(ctx, dest) {
    playNote(ctx, dest, 880, 0.04, 0, 0.06, "sine");
    playNote(ctx, dest, 1320, 0.06, 0.04, 0.06, "sine");
    playNote(ctx, dest, 1760, 0.08, 0.08, 0.05, "triangle");
  },

  // Sparkling ascending arpeggio for daily gem claim
  gem_claim(ctx, dest) {
    playNote(ctx, dest, 1047, 0.04, 0, 0.08, "triangle");
    playNote(ctx, dest, 1319, 0.04, 0.04, 0.07, "triangle");
    playNote(ctx, dest, 1568, 0.04, 0.08, 0.07, "triangle");
    playNote(ctx, dest, 2093, 0.08, 0.12, 0.09, "sine");
    playFilteredNoise(ctx, dest, 0.12, 4000, 8000, 0.03);
  },
  daily_treasure(ctx, dest) {
    playNote(ctx, dest, 1047, 0.04, 0, 0.08, "triangle");
    playNote(ctx, dest, 1319, 0.04, 0.04, 0.07, "triangle");
    playNote(ctx, dest, 1568, 0.04, 0.08, 0.07, "triangle");
    playNote(ctx, dest, 2093, 0.08, 0.12, 0.09, "sine");
    playFilteredNoise(ctx, dest, 0.12, 4000, 8000, 0.03);
  },

  // Short bright ding for score milestones during gameplay
  score_milestone(ctx, dest) {
    playNote(ctx, dest, 1568, 0.04, 0, 0.07, "sine");
    playNote(ctx, dest, 2093, 0.06, 0.03, 0.06, "sine");
  },
  menu_hover(ctx, dest) {
    // Soft, short sine tick — barely audible, just enough tactile feedback
    playNote(ctx, dest, 1800, 0.008, 0, 0.03, "sine");
  }
};

export function sfxGainForName(name: SfxName): number {
  return SFX_GAIN[name];
}

export function playSfxRecipe(
  ctx: AudioContext,
  dest: AudioNode,
  name: SfxName,
  pitchMultiplier = 1
): void {
  // Apply pitch jitter to prevent auditory fatigue
  const jitter = SFX_PITCH_JITTER[name] ?? 0;
  const jitteredPitch = jitter > 0
    ? pitchMultiplier * (1 + (Math.random() * 2 - 1) * jitter)
    : pitchMultiplier;
  SFX_RECIPES[name](ctx, dest, jitteredPitch);
}
